import {
  IChartSettings,
  IParsedChartData,
  IParsedChartDataRecord,
  IRetentionTable,
  RetentionPostfixOnColumnName,
} from '../interfaces';
import { IBreakdownOverallStatus } from '../../../utils/interfaces';
import { DATA_KEY } from '../constants/utils';
import { assertCondition, assertNotNil } from '../../../utils/types';
import {
  IRetentionSliceFieldsFragment,
  QueryType,
} from '../../../utils/apollo/graphqlSchema';
import {
  getMeasureColorFromColorConfig,
  getSliceColorFromColorConfig,
  IAnalysisColorConfig,
  isShouldUseMeasureColor,
} from '../utils/analysisColorConfig';
import useFilteredRetentionTable from '../RetentionChart/utils/useFilteredRetentionTable';
import { IChartLine } from './useChartLines';
import { getHeaderName } from '../../../utils/redux/transformRetentionData';
import { useMemo } from 'react';

interface IResult {
  retentionLines: IChartLine[];
  data: IParsedChartDataRecord[];
}

export interface IRetentionLine extends IChartLine {
  sliceKey: string;
}

const generateVisibleRetentionLines = (
  filteredTable: IRetentionTable,
  isBreakdownOverall: IBreakdownOverallStatus,
  colorConfig: IAnalysisColorConfig,
): IRetentionLine[] => {
  const retentionLines: IRetentionLine[] = [];

  const shouldUseMeasureColor = isShouldUseMeasureColor(isBreakdownOverall);

  for (const row of filteredTable.rows) {
    const rowHeader = row.header;

    const color = shouldUseMeasureColor
      ? getMeasureColorFromColorConfig(colorConfig, rowHeader.key)
      : getSliceColorFromColorConfig(colorConfig, row.sliceKey);

    retentionLines.push({
      name: row.key,
      display: getHeaderName(rowHeader),
      color,
      measureKey: rowHeader.key,
      type: 'normal',
      sliceKey: row.sliceKey,
    });
  }

  return retentionLines;
};

/**
 * generate retention data from chartData and chartSettings
 * @returns data of retention line/bar chart from giving settings.
 */
const generateRetentionData = (
  table: IRetentionTable,
  chartSettings: IChartSettings,
): IParsedChartDataRecord[] => {
  const retentionData: IParsedChartDataRecord[] = [];
  const { isChurn } = chartSettings;

  for (let i = 0; i < table.columnHeaders.length; i++) {
    const columnHeader = table.columnHeaders[i];
    retentionData[i] = { [DATA_KEY]: getHeaderName(columnHeader) };
  }

  for (const row of table.rows) {
    for (let i = 0; i < table.columnHeaders.length; i++) {
      const dataRecord = retentionData[i];
      const countKey = row.key;
      const percentKey = row.key + RetentionPostfixOnColumnName.percentKey;
      if (i < row.returns.length) {
        const cell = row.returns[i];
        const { churnCount, churnPercent, retentionCount, retentionPercent } =
          cell;
        dataRecord[countKey] = isChurn ? churnCount : retentionCount;
        dataRecord[percentKey] = isChurn ? churnPercent : retentionPercent;
      } else {
        dataRecord[percentKey] = null;
      }
    }
  }

  return retentionData;
};

const sortRetentionLinesBySliceOrder = (
  retentionLines: IRetentionLine[],
  slices: IRetentionSliceFieldsFragment[],
) => {
  const slicesByIndex = slices.reduce((acc, slice, index) => {
    acc.set(slice.key, index);
    return acc;
  }, new Map<string, number>());
  retentionLines.sort((a, b) => {
    const aSliceIndex = slicesByIndex.get(a.sliceKey);
    assertNotNil(aSliceIndex);
    const bSliceIndex = slicesByIndex.get(b.sliceKey);
    assertNotNil(bSliceIndex);
    return aSliceIndex - bSliceIndex;
  });
};

export default (
  chartData: IParsedChartData,
  chartSettings: IChartSettings,
  isBreakdownOverall: IBreakdownOverallStatus,
  colorConfig: IAnalysisColorConfig,
): IResult => {
  // narrow down the chartData type to retention.
  assertCondition(chartData.queryType === QueryType.Retention);

  const table = useFilteredRetentionTable({
    chartData,
    chartSettings,
    onlyVisibleMeasures: true,
  });

  const { slices } = chartData;
  return useMemo(() => {
    const data = generateRetentionData(table, chartSettings);

    const retentionLines = generateVisibleRetentionLines(
      table,
      isBreakdownOverall,
      colorConfig,
    );

    // table rows are sorted by cohort/return period, then by slices
    // however, the tooltips follow the chart lines order which everywhere
    // is by slice first, then by measures.
    // IMPORTANT: this function modifies retentionLines in place
    sortRetentionLinesBySliceOrder(retentionLines, slices);

    return {
      retentionLines,
      data,
    };
  }, [slices, chartSettings, colorConfig, isBreakdownOverall, table]);
};
